import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { buildLdSurveySubmitBodyFromStorage, submitCustomerSurveyTng } from '@/api/customerSurvey';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import {
  readOtherPrevBrandAnswerAnswers,
  writeOtherPrevBrandAnswerAnswers,
  type OtherPrevBrandAnswerAnswers,
} from '@/lib/otherPrevBrandAnswerStorage';
import { isOtherPrevBrandComplete, readOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { readPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyBeforeOtherProductRedirect, surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const hintClass = 'm-0 text-left text-[14px] leading-tight text-black/80 sm:text-[15px]';

const sliderClass =
  'h-2 w-full cursor-pointer appearance-none rounded-full bg-green-600 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-black [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-black';

const REASON_GROUP = 'other-prev-brand-why-reason';

type ElaboratedKey = 'taste' | 'price' | 'productFeatures';
type SimpleReasonKey = 'brandCredibility' | 'peersSmoking';

function clearAllReasonSelections(): Pick<
  OtherPrevBrandAnswerAnswers,
  'taste' | 'price' | 'productFeatures' | 'brandCredibility' | 'peersSmoking' | 'otherReason'
> {
  return {
    taste: { checked: false, elaboration: '' },
    price: { checked: false, elaboration: '' },
    productFeatures: { checked: false, elaboration: '' },
    brandCredibility: false,
    peersSmoking: false,
    otherReason: '',
  };
}

function ElaboratedOption({
  id,
  label,
  checked,
  elaboration,
  hint,
  onSelect,
  onElaborationChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  elaboration: string;
  hint: string;
  onSelect: () => void;
  onElaborationChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
        <input
          id={id}
          type="radio"
          name={REASON_GROUP}
          checked={checked}
          onChange={onSelect}
          className={surveyChoiceInputClass}
        />
        <span className={labelClass}>{label}</span>
      </label>
      <p className={hintClass}>{hint}</p>
      <input
        type="text"
        value={elaboration}
        onChange={(e) => onElaborationChange(e.target.value)}
        onFocus={onSelect}
        className={inputClass}
      />
    </div>
  );
}

function SimpleRadio({
  id,
  label,
  checked,
  onSelect,
}: {
  id: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
      <input
        id={id}
        type="radio"
        name={REASON_GROUP}
        checked={checked}
        onChange={onSelect}
        className={surveyChoiceInputClass}
      />
      <span className={labelClass}>{label}</span>
    </label>
  );
}

function formatBrandVariantLabel(brand?: string, variant?: string): string | null {
  const b = brand?.trim();
  const v = variant?.trim();
  if (!b || !v) return null;
  return `${b} - ${v}`;
}

export default function PrevBrandAnswerOther() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet, mobileLocalDigits } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<OtherPrevBrandAnswerAnswers>(() =>
    readOtherPrevBrandAnswerAnswers()
  );
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    writeOtherPrevBrandAnswerAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  const beforeOtherRedirect =
    questionnaireSet === 'A' || questionnaireSet === 'B'
      ? surveyBeforeOtherProductRedirect(questionnaireSet)
      : '/survey';
  if (beforeOtherRedirect) return <Navigate to={beforeOtherRedirect} replace />;
  if (!isOtherPrevBrandComplete()) return <Navigate to="/survey/prevbrand-other" replace />;

  const firstProductLabel =
    formatBrandVariantLabel(readPrevBrandAnswers()?.brand, readPrevBrandAnswers()?.variant) ??
    t.prevBrandAnswerOther.firstProduct;
  const secondProductLabel =
    formatBrandVariantLabel(
      readOtherPrevBrandAnswers()?.brand,
      readOtherPrevBrandAnswers()?.variant
    ) ?? t.prevBrandAnswerOther.secondProduct;

  const secondProductCount = 10 - answers.firstProductCount;

  const selectElaboratedReason = (key: ElaboratedKey) => {
    setAnswers((prev) => ({
      ...prev,
      ...clearAllReasonSelections(),
      [key]: { checked: true, elaboration: prev[key].elaboration },
    }));
    setWarning(null);
  };

  const selectSimpleReason = (key: SimpleReasonKey) => {
    setAnswers((prev) => ({
      ...prev,
      ...clearAllReasonSelections(),
      [key]: true,
    }));
    setWarning(null);
  };

  const setElaboration = (key: ElaboratedKey, elaboration: string) => {
    setAnswers((prev) => ({
      ...prev,
      ...clearAllReasonSelections(),
      [key]: { checked: true, elaboration },
    }));
    setWarning(null);
  };

  const handleOtherReasonChange = (otherReason: string) => {
    setAnswers((prev) => ({
      ...prev,
      ...clearAllReasonSelections(),
      otherReason,
    }));
    setWarning(null);
  };

  const handleSubmit = () => {
    const trimmedOther = answers.otherReason.trim();
    const hasReason =
      answers.taste.checked ||
      answers.price.checked ||
      answers.productFeatures.checked ||
      answers.brandCredibility ||
      answers.peersSmoking ||
      trimmedOther.length > 0;

    if (!hasReason) {
      setWarning(t.prevBrandAnswer.reasonRequiredWarning);
      return;
    }
    if (answers.taste.checked && !answers.taste.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.tasteElaborationWarning);
      return;
    }
    if (answers.price.checked && !answers.price.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.priceElaborationWarning);
      return;
    }
    if (answers.productFeatures.checked && !answers.productFeatures.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.productFeaturesElaborationWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.whyNotLd.sessionExpiredWarning);
      return;
    }

    writeOtherPrevBrandAnswerAnswers({ ...answers, otherReason: trimmedOther });

    const body = buildLdSurveySubmitBodyFromStorage(mobileLocalDigits);
    if (!body) {
      setWarning(t.whyNotLd.incompleteWarning);
      return;
    }

    setSubmitting(true);
    setWarning(null);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.whyNotLd.submitError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswer.whyQuestion}</p>

                <ElaboratedOption
                  id="other-reason-taste"
                  label={t.prevBrandAnswer.taste}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.taste.checked}
                  elaboration={answers.taste.elaboration}
                  onSelect={() => selectElaboratedReason('taste')}
                  onElaborationChange={(elaboration) => setElaboration('taste', elaboration)}
                />

                <ElaboratedOption
                  id="other-reason-price"
                  label={t.prevBrandAnswer.price}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.price.checked}
                  elaboration={answers.price.elaboration}
                  onSelect={() => selectElaboratedReason('price')}
                  onElaborationChange={(elaboration) => setElaboration('price', elaboration)}
                />

                <ElaboratedOption
                  id="other-reason-product-features"
                  label={t.prevBrandAnswer.productFeatures}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.productFeatures.checked}
                  elaboration={answers.productFeatures.elaboration}
                  onSelect={() => selectElaboratedReason('productFeatures')}
                  onElaborationChange={(elaboration) => setElaboration('productFeatures', elaboration)}
                />

                <SimpleRadio
                  id="other-reason-brand-credibility"
                  label={t.prevBrandAnswer.brandCredibility}
                  checked={answers.brandCredibility}
                  onSelect={() => selectSimpleReason('brandCredibility')}
                />

                <SimpleRadio
                  id="other-reason-peers"
                  label={t.prevBrandAnswer.peersSmoking}
                  checked={answers.peersSmoking}
                  onSelect={() => selectSimpleReason('peersSmoking')}
                />

                <div className="flex flex-col gap-1">
                  <label htmlFor="other-reason-other" className={labelClass}>
                    {t.prevBrandAnswer.otherReasons}
                  </label>
                  <input
                    id="other-reason-other"
                    type="text"
                    value={answers.otherReason}
                    onChange={(e) => handleOtherReasonChange(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-3">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswerOther.smokeFrequencyQuestion}</p>

                <div className="flex justify-between gap-3">
                  <span className="max-w-[48%] text-left text-[15px] font-semibold leading-tight text-black sm:text-[16px]">
                    {firstProductLabel}
                  </span>
                  <span className="max-w-[48%] text-right text-[15px] font-semibold leading-tight text-black sm:text-[16px]">
                    {secondProductLabel}
                  </span>
                </div>

                <input
                  id="smoke-frequency-slider"
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={answers.firstProductCount}
                  onChange={(e) => {
                    setAnswers((prev) => ({
                      ...prev,
                      firstProductCount: Number(e.target.value),
                    }));
                    setWarning(null);
                  }}
                  className={sliderClass}
                  aria-valuemin={0}
                  aria-valuemax={10}
                  aria-valuenow={answers.firstProductCount}
                  aria-label={t.prevBrandAnswerOther.smokeFrequencyQuestion}
                />

                <div className="flex justify-between gap-2">
                  <span className="text-[15px] font-medium text-black sm:text-[16px]">
                    {answers.firstProductCount} {t.prevBrandAnswerOther.times}
                  </span>
                  <span className="text-[15px] font-medium text-black sm:text-[16px]">
                    {secondProductCount} {t.prevBrandAnswerOther.times}
                  </span>
                </div>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-2 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={submitting} onClick={handleSubmit}>
                {t.whyNotLd.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
